import { useState, useCallback, useMemo } from 'react';
import { loadMatches, saveMatches, saveBracket } from '../utils/storage';

/**
 * useScoreLogic - Hook per la gestione centralizzata della logica punteggio
 * 
 * Estrae la logica di calcolo punteggio da ScoreKeeper.js per:
 * - Calcolo stoccate totali e per assalto
 * - Calcolo soglie cambio assalto
 * - Gestione incremento/decremento punteggi
 * - Verifica fine match
 * - Salvataggio punteggi
 */
export const useScoreLogic = ({
    matchId,
    bracketMode = false,
    roundIdx,
    matchIdx,
    assalti = [],
    stoccatePerAssalto = 5,
    match = null,
}) => {
    // === STATO PUNTEGGI ===
    const [scoreA, setScoreA] = useState(0);
    const [scoreB, setScoreB] = useState(0);
    const [priorityTeam, setPriorityTeam] = useState(null);

    // === FUNZIONI DI CALCOLO ===

    /**
     * Calcola il punteggio totale massimo raggiungibile
     */
    const getStoccateTotali = useCallback(() => {
        const competitionType = localStorage.getItem('competitionType') || 'individual';

        // Se sono presenti assalti e stoccatePerAssalto, calcola il totale reale
        if (assalti && assalti.length > 0 && stoccatePerAssalto) {
            return assalti.length * stoccatePerAssalto;
        }

        // Fallback legacy (solo se dati mancanti)
        if (bracketMode) {
            if (competitionType === 'team') {
                return 45;
            }
            return stoccatePerAssalto;
        } else if (competitionType === 'team') {
            return 45;
        }
        return stoccatePerAssalto;
    }, [assalti, stoccatePerAssalto, bracketMode]);

    /**
     * Restituisce le stoccate per assalto reali
     */
    const getStoccatePerAssaltoReale = useCallback(() => {
        return stoccatePerAssalto;
    }, [stoccatePerAssalto]);

    /**
     * Calcola le soglie per ogni cambio assalto
     */
    const getThresholds = useCallback(() => {
        if (!assalti || assalti.length === 0) {
            return [5, 10, 15, 20, 25, 30, 35, 40, 45]; // Default staffetta 9 assalti
        }
        const stoccateAssalto = stoccatePerAssalto;
        return assalti.map((_, i) => (i + 1) * stoccateAssalto);
    }, [assalti, stoccatePerAssalto]);

    /**
     * Verifica se l'incontro è terminato (punteggio massimo raggiunto)
     */
    const isMatchFinished = useCallback(() => {
        const stoccateTotali = getStoccateTotali();
        return scoreA === stoccateTotali || scoreB === stoccateTotali;
    }, [scoreA, scoreB, getStoccateTotali]);

    /**
     * Determina quale squadra è in vantaggio
     */
    const getLeadingTeam = useCallback(() => {
        if (scoreA > scoreB) return 'A';
        if (scoreB > scoreA) return 'B';
        return null; // Parità
    }, [scoreA, scoreB]);

    /**
     * Calcola la prossima soglia per cambio assalto
     */
    const getNextThreshold = useCallback(() => {
        const maxScore = Math.max(scoreA, scoreB);
        const thresholds = getThresholds();
        return thresholds.find(t => t > maxScore) || getStoccateTotali();
    }, [scoreA, scoreB, getThresholds, getStoccateTotali]);

    /**
     * Assegna la priorità manualmente (per parità a tempo scaduto)
     */
    const assignPriority = useCallback((team) => {
        if (scoreA === scoreB) {
            setPriorityTeam(team);
            return team;
        }
        return null;
    }, [scoreA, scoreB]);

    // === FUNZIONI DI AGGIORNAMENTO PUNTEGGIO ===

    /**
     * Aggiorna il punteggio e salva nel storage appropriato
     */
    const updateMatchScore = useCallback((newScoreA, newScoreB) => {
        const maxScore = getStoccateTotali();
        const isCompleted = newScoreA === maxScore || newScoreB === maxScore;

        // Non permettere che entrambi abbiano il punteggio massimo
        let finalScoreA = newScoreA;
        let finalScoreB = newScoreB;
        if (newScoreA === maxScore && newScoreB === maxScore) {
            // Mantieni l'ultimo valore valido
            finalScoreB = maxScore - 1;
        }

        // Aggiorna stato locale
        setScoreA(finalScoreA);
        setScoreB(finalScoreB);

        // Salva nel storage
        if (bracketMode && typeof roundIdx === 'number' && typeof matchIdx === 'number') {
            const bracket = JSON.parse(localStorage.getItem('bracket') || '[]');
            if (Array.isArray(bracket) && bracket[roundIdx] && bracket[roundIdx][matchIdx]) {
                bracket[roundIdx][matchIdx].scoreA = finalScoreA;
                bracket[roundIdx][matchIdx].scoreB = finalScoreB;
                bracket[roundIdx][matchIdx].score1 = finalScoreA;
                bracket[roundIdx][matchIdx].score2 = finalScoreB;
                bracket[roundIdx][matchIdx].completed = isCompleted;
                bracket[roundIdx][matchIdx].winner = finalScoreA > finalScoreB ? 'A' : 'B';
                saveBracket(bracket);
            }
        } else if (typeof matchId === 'number') {
            const matches = loadMatches() || [];
            matches[matchId] = { ...matches[matchId], scoreA: finalScoreA, scoreB: finalScoreB };
            saveMatches(matches);
        }

        window.dispatchEvent(new Event('matchesUpdated'));

        return { scoreA: finalScoreA, scoreB: finalScoreB, isCompleted };
    }, [getStoccateTotali, bracketMode, roundIdx, matchIdx, matchId]);

    /**
     * Incrementa punteggio squadra A
     */
    const increaseScoreA = useCallback(() => {
        const maxScore = getStoccateTotali();
        if (scoreA < maxScore) {
            const newScoreA = scoreA + 1;
            // Impedisci che entrambi abbiano il punteggio massimo
            if (newScoreA === maxScore && scoreB === maxScore) {
                updateMatchScore(newScoreA, maxScore - 1);
            } else {
                updateMatchScore(newScoreA, scoreB);
            }
        }
    }, [scoreA, scoreB, getStoccateTotali, updateMatchScore]);

    /**
     * Decrementa punteggio squadra A
     */
    const decreaseScoreA = useCallback(() => {
        if (scoreA > 0) {
            updateMatchScore(scoreA - 1, scoreB);
        }
    }, [scoreA, scoreB, updateMatchScore]);

    /**
     * Incrementa punteggio squadra B
     */
    const increaseScoreB = useCallback(() => {
        const maxScore = getStoccateTotali();
        if (scoreB < maxScore) {
            const newScoreB = scoreB + 1;
            // Impedisci che entrambi abbiano il punteggio massimo
            if (scoreA === maxScore && newScoreB === maxScore) {
                updateMatchScore(maxScore - 1, newScoreB);
            } else {
                updateMatchScore(scoreA, newScoreB);
            }
        }
    }, [scoreA, scoreB, getStoccateTotali, updateMatchScore]);

    /**
     * Decrementa punteggio squadra B
     */
    const decreaseScoreB = useCallback(() => {
        if (scoreB > 0) {
            updateMatchScore(scoreA, scoreB - 1);
        }
    }, [scoreA, scoreB, updateMatchScore]);

    /**
     * Imposta un punteggio direttamente (es. da numpad)
     */
    const setScore = useCallback((team, value) => {
        const maxScore = getStoccateTotali();
        const clampedValue = Math.min(Math.max(0, value), maxScore);

        if (team === 'A') {
            // Se imposto A al massimo e B è già al massimo, riduci B
            if (clampedValue === maxScore && scoreB === maxScore) {
                updateMatchScore(clampedValue, maxScore - 1);
            } else {
                updateMatchScore(clampedValue, scoreB);
            }
        } else {
            // Se imposto B al massimo e A è già al massimo, riduci A
            if (scoreA === maxScore && clampedValue === maxScore) {
                updateMatchScore(maxScore - 1, clampedValue);
            } else {
                updateMatchScore(scoreA, clampedValue);
            }
        }
    }, [scoreA, scoreB, getStoccateTotali, updateMatchScore]);

    // === VALORI CALCOLATI ===
    const stoccateTotali = useMemo(() => getStoccateTotali(), [getStoccateTotali]);
    const thresholds = useMemo(() => getThresholds(), [getThresholds]);
    const nextThreshold = useMemo(() => getNextThreshold(), [getNextThreshold]);
    const matchFinished = useMemo(() => isMatchFinished(), [isMatchFinished]);
    const leadingTeam = useMemo(() => getLeadingTeam(), [getLeadingTeam]);

    // === RETURN ===
    return {
        // Stato
        scoreA,
        scoreB,
        priorityTeam,

        // Setters diretti (per compatibilità con codice esistente)
        setScoreA,
        setScoreB,

        // Funzioni di calcolo
        getStoccateTotali,
        getStoccatePerAssaltoReale,
        getThresholds,
        isMatchFinished,
        getLeadingTeam,
        getNextThreshold,
        assignPriority,

        // Funzioni di modifica punteggio
        updateMatchScore,
        increaseScoreA,
        decreaseScoreA,
        increaseScoreB,
        decreaseScoreB,
        setScore,

        // Valori calcolati (per evitare ricalcoli)
        stoccateTotali,
        thresholds,
        nextThreshold,
        matchFinished,
        leadingTeam,
    };
};

export default useScoreLogic;
